import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CalendarIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import * as React from 'react';
import { logger } from '@sentry/react';

import { Label } from '../shadcn/label';

import { Button } from '@/shared/components/shadcn/button';
import { Calendar } from '@/shared/components/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import { cn } from '@/shared/utils/index';
import { DATE_FORMATS } from '../../utils/date/date-formats';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;

dayjs.extend(utc);
dayjs.extend(timezone);

type CalendarDateParts = {
  year: number;
  month: number;
  day: number;
};

function isValidIsoDatePart(value: string) {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  const [yearString, monthString, dayString] = value.split('-');
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  return (
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day
  );
}

function parseDateISOString(value?: DateISOString) {
  if (value == null) {
    return undefined;
  }

  if (!isValidIsoDatePart(value.slice(0, 10))) {
    logger.error('DatePicker received a non-ISO date string', {
      extra: {
        value,
      },
    });
    return undefined;
  }

  const isIsoDateString = ISO_DATE_PATTERN.test(value);
  const isIsoDateTimeString = ISO_DATE_TIME_PATTERN.test(value);

  if (!isIsoDateString && !isIsoDateTimeString) {
    logger.error('DatePicker received a non-ISO date string', {
      extra: {
        value,
      },
    });
    return undefined;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    logger.error('DatePicker received an invalid ISO date string', {
      extra: {
        value,
      },
    });
    return undefined;
  }

  return parsedDate;
}

function getLocalCalendarDateParts(date: Date): CalendarDateParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

function getZonedCalendarDateParts(
  value: DateISOString | undefined,
  timeZone?: string,
): CalendarDateParts | undefined {
  const parsedDate = parseDateISOString(value);
  if (!parsedDate) return undefined;

  if (!timeZone) {
    return getLocalCalendarDateParts(parsedDate);
  }

  const zonedDate = dayjs(parsedDate).tz(timeZone);
  return {
    year: zonedDate.year(),
    month: zonedDate.month() + 1,
    day: zonedDate.date(),
  };
}

function createLocalDisplayDate(parts: CalendarDateParts) {
  return new Date(parts.year, parts.month - 1, parts.day, 12);
}

function serializeDateSelection(date: Date | undefined, timeZone?: string) {
  if (!date) {
    return undefined;
  }

  if (!timeZone) {
    return date.toISOString();
  }

  const { year, month, day } = getLocalCalendarDateParts(date);
  const monthString = String(month).padStart(2, '0');
  const dayString = String(day).padStart(2, '0');

  return dayjs.tz(`${year}-${monthString}-${dayString}T00:00:00`, timeZone).toISOString();
}

function transformCalendarMatcherDate(date: Date, timeZone?: string) {
  if (!timeZone) {
    return date;
  }

  const parts = getZonedCalendarDateParts(date.toISOString(), timeZone);
  return parts ? createLocalDisplayDate(parts) : date;
}

function transformCalendarMatcher(
  matcher: React.ComponentProps<typeof Calendar>['disabled'],
  timeZone?: string,
): React.ComponentProps<typeof Calendar>['disabled'] {
  if (!timeZone || matcher == null || typeof matcher === 'boolean' || typeof matcher === 'function') {
    return matcher;
  }

  if (matcher instanceof Date) {
    return transformCalendarMatcherDate(matcher, timeZone);
  }

  if (Array.isArray(matcher)) {
    return matcher
      .map((value) => transformCalendarMatcher(value, timeZone))
      .filter((value) => value !== undefined) as React.ComponentProps<typeof Calendar>['disabled'];
  }

  if (typeof matcher === 'object') {
    const nextMatcher = { ...matcher } as Record<string, unknown>;

    ['before', 'after', 'from', 'to'].forEach((key) => {
      const currentValue = nextMatcher[key];

      if (currentValue instanceof Date) {
        nextMatcher[key] = transformCalendarMatcherDate(currentValue, timeZone);
      }
    });

    return nextMatcher as React.ComponentProps<typeof Calendar>['disabled'];
  }

  return matcher;
}

interface DatePickerProps {
  value?: DateISOString;
  onChange?: (date: DateISOString | undefined) => void;
  timeZone?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
  className?: string;
  spacingClassName?: string;
  label?: ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}

export const DatePicker = ({
  value,
  onChange,
  timeZone,
  placeholder = 'Pick a date',
  disabled = false,
  invalid = false,
  disabledDates,
  className,
  spacingClassName,
  label,
  ref,
  ...props
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedDate = React.useMemo(() => {
    const calendarDateParts = getZonedCalendarDateParts(value, timeZone);
    return calendarDateParts ? createLocalDisplayDate(calendarDateParts) : undefined;
  }, [timeZone, value]);
  const normalizedDisabledDates = React.useMemo(
    () => transformCalendarMatcher(disabledDates, timeZone),
    [disabledDates, timeZone],
  );

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(serializeDateSelection(date, timeZone));
    setIsOpen(false);
  };

  return (
    <div className={cn(spacingClassName ?? 'space-y-2', className)}>
      {label != null && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className={cn(
              'w-full justify-start bg-white font-normal',
              !selectedDate && 'text-zinc-500',
              selectedDate && 'text-zinc-900',
            )}
            {...props}
          >
            <CalendarIcon className="size-4" />
            {selectedDate ? dayjs(selectedDate).format(DATE_FORMATS.base) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={handleDateSelect}
            disabled={disabled ? true : normalizedDisabledDates}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

DatePicker.displayName = 'DatePicker';
